class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // Rhino v7.4.6: the header bag is a normal navigation link to /cart.
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.removeAttribute('role');
    cartLink.removeAttribute('aria-haspopup');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Rhino Spice v7.4.7: refresh drawer from Shopify AFTER the add completes.
  // The cart/add.js bundled sections can be stale when the first items are added.
  // A fresh section request prevents an empty product list beside a correct subtotal.
  async renderContents(parsedState) {
    const sequence = this._rhinoRenderSequence = (this._rhinoRenderSequence || 0) + 1;
    const base = window.Shopify?.routes?.root || '/';
    const itemsContainer = this.querySelector('#CartDrawer-CartItems');
    if (itemsContainer) itemsContainer.setAttribute('aria-busy', 'true');
    try {
      // Reading the cart first also ensures Shopify has committed the add.
      const cartResponse = await fetch(base + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      let sections = null;
      // Shopify's Section Rendering API returns the current cart HTML.
      for (let attempt = 0; attempt < 2; attempt++) {
        const response = await fetch(base + '?sections=cart-drawer,cart-icon-bubble', {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Cart sections unavailable');
        const candidate = await response.json();
        const html = candidate['cart-drawer'];
        const documentFragment = html ? new DOMParser().parseFromString(html, 'text/html') : null;
        const drawerMarkup = documentFragment?.querySelector('#CartDrawer');
        const renderedItems = drawerMarkup?.querySelectorAll('#CartDrawer-CartItems .cart-item').length || 0;
        if (drawerMarkup && (cart.items.length === 0 || renderedItems >= cart.items.length)) {
          sections = candidate;
          break;
        }
      }
      if (sequence !== this._rhinoRenderSequence) return;
      if (!sections) throw new Error('Cart items not yet available');
      const drawerHTML = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('#CartDrawer');
      const currentDrawer = this.querySelector('#CartDrawer');
      if (!drawerHTML || !currentDrawer) throw new Error('Cart drawer markup missing');
      currentDrawer.innerHTML = drawerHTML.innerHTML;
      this.classList.toggle('is-empty', cart.item_count === 0);
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const updated = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html')
          .querySelector('#cart-icon-bubble');
        if (updated) bubble.innerHTML = updated.innerHTML;
      }
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
      document.dispatchEvent(new CustomEvent('rhino:cart-updated'));
    } catch (error) {
      console.error('Rhino cart drawer refresh:', error);
      // Do not show a blank drawer if Shopify could not render its products.
      // The full cart page remains the reliable fallback.
      window.location.assign(base + 'cart');
    } finally {
      this.querySelector('#CartDrawer-CartItems')?.removeAttribute('aria-busy');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
